import { useCallback, useEffect, useRef, useState } from "react";
import { useApiFetch } from "../../../api/client";
import { errorMessage } from "../../cockpit/format";
import type { GradeTdsAttachResult, NomenclatureUpload } from "../types";

const FILES_PER_REQUEST = 5;

export type HeldTdsState = "held" | "attaching" | "attached" | "alreadyAttached" | "failed";

export interface HeldTdsFile {
  heldFileId: number;
  uploadRowId: number;
  file: File;
  state: HeldTdsState;
  failureText: string | null;
}

export function useHeldTdsFiles(upload: NomenclatureUpload | null, onAttached: () => void) {
  const apiFetch = useApiFetch();
  const apiFetchRef = useRef(apiFetch);
  apiFetchRef.current = apiFetch;
  const onAttachedRef = useRef(onAttached);
  onAttachedRef.current = onAttached;
  const nextHeldFileIdRef = useRef(1);
  const [heldFiles, setHeldFiles] = useState<HeldTdsFile[]>([]);
  const batchGuid = upload?.batchGuid ?? null;

  useEffect(() => {
    setHeldFiles([]);
  }, [batchGuid]);

  const updateHeldFiles = useCallback(
    (heldFileIds: number[], changes: Partial<HeldTdsFile>) =>
      setHeldFiles((currentFiles) =>
        currentFiles.map((heldFile) =>
          heldFileIds.includes(heldFile.heldFileId) ? { ...heldFile, ...changes } : heldFile,
        ),
      ),
    [],
  );

  useEffect(() => {
    if (!upload) return;
    const gradeIdByUploadRowId = new Map(
      upload.rows.filter((row) => row.gradeId != null).map((row) => [row.uploadRowId, row.gradeId as number]),
    );
    const readyFiles = heldFiles.filter(
      (heldFile) => heldFile.state === "held" && gradeIdByUploadRowId.has(heldFile.uploadRowId),
    );
    if (!readyFiles.length) return;
    updateHeldFiles(
      readyFiles.map((heldFile) => heldFile.heldFileId),
      { state: "attaching", failureText: null },
    );

    const attachReadyFiles = async () => {
      let anyAttached = false;
      for (let startIndex = 0; startIndex < readyFiles.length; startIndex += FILES_PER_REQUEST) {
        const requestFiles = readyFiles.slice(startIndex, startIndex + FILES_PER_REQUEST);
        const formData = new FormData();
        requestFiles.forEach((heldFile) => formData.append("files", heldFile.file));
        formData.append(
          "links",
          JSON.stringify(
            requestFiles.map((heldFile, fileIndex) => ({
              fileIndex,
              gradeId: gradeIdByUploadRowId.get(heldFile.uploadRowId),
            })),
          ),
        );
        try {
          const response = await apiFetchRef.current("nomenclature/grade-tds", { method: "POST", body: formData });
          const results: GradeTdsAttachResult[] = await response.json();
          for (const [fileIndex, heldFile] of requestFiles.entries()) {
            const isAlreadyAttached =
              results.find((attachResult) => attachResult.fileIndex === fileIndex)?.status === "AlreadyAttached";
            updateHeldFiles([heldFile.heldFileId], { state: isAlreadyAttached ? "alreadyAttached" : "attached" });
          }
          anyAttached = true;
        } catch (error) {
          updateHeldFiles(
            requestFiles.map((heldFile) => heldFile.heldFileId),
            { state: "failed", failureText: errorMessage(error) },
          );
        }
      }
      if (anyAttached) onAttachedRef.current();
    };
    void attachReadyFiles();
  }, [upload, heldFiles, updateHeldFiles]);

  const addFiles = (uploadRowId: number, files: File[]) =>
    setHeldFiles((currentFiles) => [
      ...currentFiles,
      ...files.map((file) => ({
        heldFileId: nextHeldFileIdRef.current++,
        uploadRowId,
        file,
        state: "held" as const,
        failureText: null,
      })),
    ]);

  const removeFile = (heldFileId: number) =>
    setHeldFiles((currentFiles) => currentFiles.filter((heldFile) => heldFile.heldFileId !== heldFileId));

  const retryFile = (heldFileId: number) => updateHeldFiles([heldFileId], { state: "held", failureText: null });

  return { heldFiles, addFiles, removeFile, retryFile };
}
